import React from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/subpage.css";
import "../../Styles/pages/pricing.css";
import useReveal from "./useReveal";

export default function Pricing() {
  const ref = useReveal();

  return (
    <div className="page ann-subpage" ref={ref}>
      <section className="sub-hero" id="top">
        <span className="eye">Pricing</span>
        <h1>Priced to your <em>roles</em>, not per seat<span className="period" /></h1>
        <p className="lede-xl">
          Ann isn't billed per login. It's priced to the <b>hiring you actually do</b> — how much of the pipeline you hand over, and at what volume. Start with one bottleneck or run the whole thing. <em>We'll size it to your team.</em>
        </p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Get a quote</Link>
          <a href="#factors" className="btn btn-secondary btn-lg">What shapes the price</a>
        </div>
      </section>

      {/* TIERS */}
      <section className="sub-section">
        <div className="sub-head">
          <span className="eye">Where to start</span>
          <h2 className="sub-h2">Take one part. Or the <em>whole</em> pipeline.</h2>
          <p className="lede">Three ways to engage Ann. Same intelligence underneath — you choose how much of the work it runs.</p>
        </div>
        <div className="tiers">
          <div className="tier">
            <div className="lbl">Sourcing</div>
            <h3>Find</h3>
            <p className="for">When sourcing is your bottleneck.</p>
            <div className="price">Scoped to volume</div>
            <div className="pricek">by roles &amp; candidates / month</div>
            <div className="stages"><div className="step on">Find</div><div className="step off">Eval</div><div className="step off">Inter</div><div className="step off">Dec</div></div>
            <ul>
              <li>Evidence-based sourcing</li>
              <li>The hidden ~60% surfaced</li>
              <li>Works alongside your stack</li>
              <li>ATS sync included</li>
            </ul>
            <Link to="/contact-us" className="btn btn-secondary">Talk to us</Link>
          </div>

          <div className="tier featured">
            <span className="ribbon">Most teams</span>
            <div className="lbl">End to end</div>
            <h3>The <em>whole</em> pipeline</h3>
            <p className="for">When you want hiring as one connected intelligence.</p>
            <div className="price">Scoped to your hiring</div>
            <div className="pricek">by roles, volume &amp; seats served</div>
            <div className="stages"><div className="step on">Find</div><div className="step on">Eval</div><div className="step on">Inter</div><div className="step on">Dec</div></div>
            <ul>
              <li>Everything in Find &amp; Interview</li>
              <li>Full Find→Decide pipeline</li>
              <li>Confidence reports &amp; evidence trail</li>
              <li>Priority support &amp; onboarding</li>
            </ul>
            <Link to="/contact-us" className="btn btn-primary">Get a quote</Link>
          </div>

          <div className="tier">
            <div className="lbl">Interviews</div>
            <h3>Interview</h3>
            <p className="for">When evaluation drifts between interviewers.</p>
            <div className="price">Scoped to volume</div>
            <div className="pricek">by interviews / month</div>
            <div className="stages"><div className="step off">Find</div><div className="step off">Eval</div><div className="step on">Inter</div><div className="step on">Dec</div></div>
            <ul>
              <li>Structured, recorded rounds</li>
              <li>One rubric across candidates</li>
              <li>Confidence report per candidate</li>
              <li>Bring your own pipeline</li>
            </ul>
            <Link to="/contact-us" className="btn btn-secondary">Talk to us</Link>
          </div>
        </div>
        <p className="prose" style={{ marginTop: "28px", fontSize: "13px", color: "var(--ink-4)", fontFamily: "var(--f-mono)", letterSpacing: ".04em" }}>// No public price list yet — Ann is in beta with design partners, and we price to fit. A quote takes one call.</p>
      </section>

      {/* WHAT SHAPES THE PRICE */}
      <section className="sub-section bone" id="factors">
        <div className="sub-head">
          <span className="eye">How it's calculated</span>
          <h2 className="sub-h2">What shapes <em>your</em> price.</h2>
          <p className="lede">No per-seat tax on inviting your team. Three things actually move the number.</p>
        </div>
        <div className="factors">
          <div className="point">
            <div className="num">01 · Scope</div>
            <h3>How much you <em>hand over</em>.</h3>
            <p>Sourcing only, interviews only, or the whole pipeline. The more of the work Ann runs, the more it's doing — and the more it's worth.</p>
          </div>
          <div className="point">
            <div className="num">02 · Volume</div>
            <h3>How much you <em>hire</em>.</h3>
            <p>Two senior searches a quarter and five thousand seats a year are different problems. Pricing tracks the hiring you actually run, not a flat tier.</p>
          </div>
          <div className="point">
            <div className="num">03 · Shape</div>
            <h3>How your <em>team</em> works.</h3>
            <p>In-house team, multi-client agency desk, embedded RPO — each is priced to its model. Unusual setup? Bring it; we're flexible.</p>
          </div>
        </div>
      </section>

      {/* PRICING FAQ */}
      <section className="sub-section">
        <div className="sub-head">
          <span className="eye">Pricing questions</span>
          <h2 className="sub-h2">Before you <em>ask</em>.</h2>
        </div>
        <div style={{ maxWidth: "860px" }}>
          <details className="faq-item" open>
            <summary><span className="num">01</span><span className="q">Why isn't there a public price?</span><span className="ico">+</span></summary>
            <p className="a">Ann is in beta with design partners, and hiring needs vary widely by scope and volume. Rather than post a number that fits no one, we size a quote to your team — it takes one call, with no obligation.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">02</span><span className="q">Do you charge per user seat?</span><span className="ico">+</span></summary>
            <p className="a">No. Inviting your whole team shouldn't cost more — collaboration is the point. Ann is priced to the hiring you do, not the number of logins.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">03</span><span className="q">Can we start small and expand?</span><span className="ico">+</span></summary>
            <p className="a">Yes. Most teams start with the single bottleneck that hurts most — sourcing or interviews — and add the rest of the pipeline within 60 days once they've seen it work.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">04</span><span className="q">Is there a pilot?</span><span className="ico">+</span></summary>
            <p className="a">We start most engagements with a real role off your desk, so you see Ann's output on your own data before committing. Bring a live role to the demo and we'll run it.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">05</span><span className="q">How are staffing agencies priced?</span><span className="ico">+</span></summary>
            <p className="a">By desk and volume, not per placement — so the economics improve as you scale. See the <Link to="/for/staffing-firms" style={{ color: "oklch(0.45 0.10 200)", textDecoration: "underline" }}>staffing page</Link> for how Ann fits contingency, RPO, and retained models.</p>
          </details>
        </div>
      </section>

      {/* CTA */}
      <section className="sub-cta">
        <span className="eye">One call, one quote</span>
        <h2>Let's size it to <em>your</em> team.</h2>
        <p className="sub">Fifteen minutes. Bring a real role; leave with a sense of the output and a quote that fits how you actually hire.</p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Get a quote</Link>
          <Link to="/how-it-works" className="btn btn-secondary btn-lg">How Ann works</Link>
        </div>
      </section>
    </div>
  );
}
